/**
 * One onboarding file, in full — walk 4's "A file: what's left to do" (#2267/#2268), on top of
 * phase 530's (#1939) file view.
 *
 * <p>The approved design reads top to bottom: the file and its state, a stage stepper, then
 * <b>What's left to do</b> (each open item: who owes it, how to resolve it, when it is due, and the
 * buttons the viewer may press), <b>People on this file</b> (form, identity check, screening),
 * <b>What has happened</b> (milestones, with every event one click away), and beside them
 * <b>About the client</b> and <b>Risk</b>. One read, one envelope
 * (`useJobStatusDetail` → `GET /internal/v0/job-status/{jobId}`).</p>
 *
 * <p>DEGRADES against an engine older than walk 4: without `open_items`, `people`, `milestones` or
 * `stage`, the page draws phase 530's outstanding block, party ledger and timeline in their place
 * rather than crashing or showing empty panels.</p>
 *
 * <h3>The three vocabularies, and where each one's English lives</h3>
 * <ul>
 *   <li><b>Declared</b> — `stage_label`, `next_action`, the parties' `roles`, an outstanding
 *       item's `subject`. The tenant wrote these; they are rendered straight through and this
 *       page never re-words them.</li>
 *   <li><b>The platform's closed sets</b> — one owner each, and no second copy anywhere:
 *       `job_parties.status` → `./partyChips.ts`, the milestone `kind` → `./milestoneCopy.ts`,
 *       the declared stage `owner` → `./FilesBoardPage.tsx`'s `OWNER_COPY`. The two this page
 *       introduces, because it is the first surface to render them, are {@link
 *       OUTSTANDING_STATUS_COPY} and {@link CONTACT_STATE_COPY} — same shape as the others: a
 *       closed map with a fallback that prints the raw token, so a value the platform grows later
 *       shows up ugly rather than invisibly.</li>
 *   <li><b>Machine vocabulary that stays machine vocabulary</b> — a state id the declaration has
 *       not named is shown PLAINLY and marked, never dressed as a sentence (AC-6).</li>
 * </ul>
 *
 * <h3>What the page refuses to do</h3>
 * <ul>
 *   <li><b>No job id in anything a staffer reads</b> (AC-5). The id is in the address bar and in
 *       the read's querystring — a routing key, not copy — and nowhere else.
 *       `FileDetailPage.test.tsx` walks the rendered tree and fails on any GUID-shaped string.</li>
 *   <li><b>No invented absence.</b> No parties reads "no parties registered yet"; an unscored file
 *       says the risk assessment has not been done, and never shows a band nobody computed; a
 *       timeline says how many further events it is NOT showing, and says nothing at all when
 *       there are none.</li>
 *   <li><b>No button a viewer may not press.</b> Every action is drawn only when the engine
 *       computed `allowed: true` for this viewer; a role-less viewer sees the items, no buttons.</li>
 * </ul>
 *
 * <p>Design tokens only — no raw hex (test/polish/hex-fence.test.ts).</p>
 */
import { useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Button,
  EmptyState,
  Icon,
  KV,
  LastUpdated,
  PageHeader,
  Pill,
  StageStepper,
  StatusBadge,
  Table,
  type Column,
  type PillTone,
  type StageStep,
} from "@ardept/design-system";
import { ApiError } from "../api/fetcher.js";
import { useClientTenant, useFamilies, useJobStatusDetail } from "../api/portalQueries.js";
import type {
  JobStatusAboutLine,
  JobStatusAttention,
  JobStatusDetailResponse,
  JobStatusMilestone,
  JobStatusOpenItem,
  JobStatusOutstandingItem,
  JobStatusParty,
  JobStatusPerson,
  JobStatusRisk,
  JobStatusStage,
} from "../api/types.js";
import { ONBOARDING_LABEL, boardFor, boardRoute } from "../nav/familyRegistry.js";
import { usePageTitle } from "../nav/usePageTitle.js";
import { NO_CLIENT_NAME, daysOpen, daysOpenLabel, ownerLabel } from "./FilesBoardPage.js";
import { actorName } from "./actorWords.js";
import { arrivedVia } from "./arrivedVia.js";
import { dueLine, momentLabel, openedLabel } from "./dates.js";
import { useFileActions } from "./fileActions.js";
import { RecordSummary } from "./declaredActions.js";
import { milestoneLine } from "./milestoneCopy.js";
import { partyRowChipFor } from "./partyChips.js";
import { PersonStatus, ScreeningStatus } from "./personStatus.js";
// The party status chip is the BOARD's chip — the same five tones, painted from the same meaning,
// because it is the same vocabulary rendered twice. Imported explicitly rather than inherited
// through the module graph, so deleting the value import above could never silently unstyle it.
import "./FilesBoardPage.css";
import "./FileDetailPage.css";

// ── Copy this page owns ──────────────────────────────────────────────────────────────────

/** What the parties block says for a file nobody has been registered on yet (AC-6). */
export const NO_PARTIES = "No parties registered yet.";

/** What the risk block says for a file nobody has scored (AC-6). Never a band, never a blank. */
export const NO_RISK = "The risk assessment has not been done yet.";

/** What a value shows when the platform holds nothing for it. One voice for every absence. */
const NOT_RECORDED = "not recorded";

/** The marker on a stage the declaration has not named (AC-6) — the board's word, reused. */
const STAGE_NOT_NAMED = "not named yet";

/** Where the back link goes, and what it says. The board is this page's only parent. */
const BACK_TO_BOARD = "All files";

// ── The outstanding-item status vocabulary, in English ───────────────────────────────────
//
// `job_outstanding_items.status` is a CLOSED column vocabulary — the
// `job_outstanding_items_status_check` CHECK of migration 069, mirrored in code by
// `OutstandingItemVocabulary.Statuses` — and the engine passes it through RAW for the same reason
// it passes `job_parties.status` through raw: the English belongs to the surface.
//
// The operator console renders these four tokens as-is (`OutstandingItemsPanel.tsx`) and is right
// to: its reader is an operator who works in the platform's own words. This page's reader is the
// client firm's own staff, so the same four facts are said in theirs. That is a difference in
// AUDIENCE, not a second opinion about what the tokens mean.

/** What each `job_outstanding_items.status` means to the firm that is owed it. */
export const OUTSTANDING_STATUS_COPY: Readonly<Record<string, string>> = {
  open: "still owed",
  supplied: "supplied",
  waived: "waived",
  not_applicable: "no longer needed",
};

/** The English for an item status, or the raw token when the platform grows a fifth. */
export function outstandingStatusLabel(status: string): string {
  return OUTSTANDING_STATUS_COPY[status] ?? status;
}

/** The one status that still HOLDS the file — everything else is the record of how it stopped. */
const STATUS_OPEN = "open";

// ── The contact-state vocabulary, in English ─────────────────────────────────────────────
//
// `JobStatusParty.contact_state` is DERIVED by the engine inside SQL, precisely so that the
// party's email address never leaves the database (Pattern S6 — `JobStatusView.cs`'s ContactState:
// "derived, never raw PII"). Three members, and the distinction that matters to a staffer is
// between the first and the third: "we know we cannot reach them" is an action, "nothing recorded
// either way" is a question.

/** What each derived contact state means to someone who has to chase the party. */
export const CONTACT_STATE_COPY: Readonly<Record<string, string>> = {
  awaiting_contact: "no way to reach them yet",
  contactable: "we can reach them",
  unknown: "nothing recorded",
};

/** The English for a contact state, or the raw token when the platform grows a fourth. */
export function contactStateLabel(state: string): string {
  return CONTACT_STATE_COPY[state] ?? state;
}

// ── Party roles ──────────────────────────────────────────────────────────────────────────
//
// `job_parties.roles` is the party's COMPLETE role set (migration 064), whose members are the
// `party_type` CHECK vocabulary — director | beneficial_owner | company_officer | psc — plus
// "whatever an unknown declared token happens to be", which that migration says is deliberately
// unconstrained. So this map covers the four the column is policed on and prints anything else as
// written: a declared role is a tenant's own word and this page has no standing to re-spell it.

/** The four roles the platform's own vocabulary names, in the words a staffer uses. */
export const ROLE_COPY: Readonly<Record<string, string>> = {
  director: "director",
  beneficial_owner: "beneficial owner",
  company_officer: "company officer",
  psc: "person with significant control",
  // Walk 3 (migration 074) — a company that owns or controls the client.
  corporate_owner: "company that owns the client",
};

/** The English for one role token, or the token itself when it is a declared one. */
export function roleLabel(role: string): string {
  return ROLE_COPY[role] ?? role;
}

// ── Ages and dates ───────────────────────────────────────────────────────────────────────

/**
 * How long something has been owed, derived at render from `since` against the page's own clock —
 * the same derivation, and for the same reason, as the board's "days open": an elapsed time
 * computed on a server is correct only at the microsecond it was computed.
 *
 * <p>Coarse on purpose. "How long has this been outstanding" is the question a staffer asks, and a
 * timestamp makes them do the subtraction. An unreadable instant says so rather than rendering a
 * number derived from NaN.</p>
 */
export function owedForLabel(since: string, now: number): string {
  const days = daysOpen(since, now);
  if (days === null) return "an unknown length of time";
  if (days === 0) return "less than a day";
  return days === 1 ? "1 day" : `${days} days`;
}

/**
 * An instant as a DATE a person reads, in UTC.
 *
 * <p>UTC rather than the browser's zone, deliberately: a timeline is read for its ORDER and its
 * rough when, and a local-zone render would put the same file's history on two different dates for
 * two colleagues in two offices. The time of day is left off for the same reason — it would be the
 * only thing on the page whose correctness depended on where the reader is sitting.</p>
 */
export function formatDay(iso: string): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return NOT_RECORDED;
  return when.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

// ── Small shared pieces ──────────────────────────────────────────────────────────────────

/** A file's name as this portal says it: the declared one, or the honest absence — never an id. */
export function fileDisplayName(clientName: string | null): { text: string; named: boolean } {
  const named = clientName !== null && clientName.trim() !== "";
  return { text: named ? (clientName as string) : NO_CLIENT_NAME, named };
}

/**
 * The stage, in the client's words — or plainly, when the declaration has none for it.
 *
 * <p>The same rule and the same treatment as the board's stage cell: a DECLARED stage is a
 * sentence the tenant wrote and gets the badge; a raw engine state id is shown as machine
 * vocabulary and marked (AC-6).</p>
 */
function StageValue({
  label,
  source,
}: {
  label: string;
  source: "declared" | "state_id";
}): JSX.Element {
  if (source === "state_id") {
    return (
      <span className="fb-stage fb-stage--raw">
        <code className="fb-stage__state">{label}</code>
        <span className="fb-stage__note">{STAGE_NOT_NAMED}</span>
      </span>
    );
  }
  return (
    <span className="fb-stage">
      <StatusBadge status={label} />
    </span>
  );
}

/**
 * One party's `job_parties.status`, as the board's chip — one vocabulary, one look. A corporate
 * owner reads "company owner: no individual check" + its company screen, never "verified".
 */
function PartyStatusChip({ party }: { party: JobStatusParty }): JSX.Element {
  const chip = partyRowChipFor(party);
  return (
    <span className={`fb-chip fb-chip--${chip.tone}`} data-party-status={chip.status}>
      <span className="fb-chip__label">{chip.label}</span>
    </span>
  );
}

/**
 * One thing a file owes, or owed — the row both this page and the cross-file chase list render.
 *
 * <p>ONE component for both, because it is one record: the chase list's rows nest the very
 * `JobStatusOutstandingItem` this page's `outstanding_items[]` carries, "the same shape, under the
 * same keys, through the same mapper" (`JobStatusOutstandingRowDto`). Two renderers for it would
 * be the drift the engine's composition was chosen to prevent, re-introduced one layer out.</p>
 *
 * <p>A WAIVE is the compliance-bearing half: who accepted the file without the item, and why, in
 * their own words. Each half is guarded separately so a half-written row cannot render an
 * attribution followed by empty quotes.</p>
 */
export function OutstandingItemLine({
  item,
  now,
}: {
  item: JobStatusOutstandingItem;
  now: number;
}): JSX.Element {
  const owed = item.status === STATUS_OPEN;
  const overdue = item.due_at !== undefined && new Date(item.due_at).getTime() < now;
  // The DECLARED label. `kind` is the fallback ONLY because the party arm's display name is
  // genuinely nullable — an honest engine token beats an invented name.
  const subject = item.subject !== null && item.subject.trim() !== "" ? item.subject : item.kind;

  return (
    <li className={`oi-item${owed ? " oi-item--owed" : ""}`}>
      <div className="oi-item__head">
        <span className="oi-item__subject">{subject}</span>
        <span className={`oi-item__status oi-item__status--${owed ? "open" : "settled"}`}>
          {outstandingStatusLabel(item.status)}
        </span>
      </div>
      <div className="oi-item__meta">
        {/* Who is chased, in the second person the board's owner column reads in — the SAME map,
            because `owner_kind` (client | party | staff) is a subset of the declared stage owner
            vocabulary and a staffer should not meet two words for one role. */}
        <span className="oi-item__who">With {ownerLabel(item.owner) ?? item.owner}</span>
        <span className="oi-item__age">
          {owed
            ? `Owed for ${owedForLabel(item.since, now)}`
            : `Raised ${owedForLabel(item.since, now)} ago`}
        </span>
        {item.due_at !== undefined && (
          <span className={`oi-item__due${overdue ? " oi-item__due--overdue" : ""}`}>
            {overdue ? `Overdue since ${formatDay(item.due_at)}` : `Due ${formatDay(item.due_at)}`}
          </span>
        )}
      </div>
      {item.waived_by !== undefined && (
        <p className="oi-item__waive">
          Waived by {actorName(item.waived_by)}
          {item.waive_reason !== undefined && (
            <span className="oi-item__waive-reason"> - &ldquo;{item.waive_reason}&rdquo;</span>
          )}
        </p>
      )}
    </li>
  );
}

/**
 * The ledger row's WHOLE identity, in the engine's own order: `(form_id, item_key, iteration_key)`.
 *
 * <p>`kind` alone is not unique across declaring forms, so two forms each declaring the same
 * supply-later id for the same party would share a key and React would treat them as one row. The
 * empty `form_id` is the party arm, which has no declaring form and cannot collide with a ledger
 * row (its `kind` is `party_&lt;status&gt;`). A React key never reaches the DOM, so this is not a
 * rendered id.</p>
 */
export function outstandingItemKey(item: JobStatusOutstandingItem): string {
  return `${item.form_id ?? ""}|${item.kind}|${item.iteration_key}`;
}

// ── The blocks ───────────────────────────────────────────────────────────────────────────

/** A titled section of the file. One shape for every block, so they read as one page. */
function Block({
  title,
  meta,
  aside,
  children,
  className,
}: {
  title: string;
  meta?: string;
  /** Something on the heading line's far side (the history's "Show every event"). */
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <section className={`fd-block${className ? ` ${className}` : ""}`}>
      <div className="fd-block__head">
        <h2 className="fd-block__title">{title}</h2>
        {meta !== undefined && <span className="fd-block__meta">{meta}</span>}
        {aside}
      </div>
      {children}
    </section>
  );
}

// ── What's left to do (walk 4) ─────────────────────────────────────────────────────────

/**
 * Who owes an item, as the owner tag says it: "You", "The client", or the person's SHORT name
 * ("Martin C.") — card 2404: a full legal name truncated in the tag ("Martin Robert Che…"). The
 * short name is the ENGINE's (`owed_by.short_name`, which also tells two same-short people on one
 * file apart); the page never shortens a name itself. An engine that sends no short name gets the
 * full name, as before.
 */
export function owedByLabel(item: JobStatusOpenItem): string {
  if (item.owed_by.kind === "staff") return "You";
  const short = typeof item.owed_by.short_name === "string" ? item.owed_by.short_name.trim() : "";
  if (short !== "") return short;
  if (item.owed_by.kind === "client") return "The client";
  return item.owed_by.name.trim() === "" ? "A person on the file" : item.owed_by.name;
}

/**
 * The owner's FULL name, for the tag's hover title and its accessible name — or undefined when the
 * tag already says everything (the viewer's own "You", or a name that is not shortened).
 */
export function owedByFullName(item: JobStatusOpenItem): string | undefined {
  if (item.owed_by.kind === "staff") return undefined;
  const full = item.owed_by.name.trim();
  return full === "" || full === owedByLabel(item) ? undefined : full;
}

/** The pill tone for who owes an item: the viewer's side reads as attention, anyone else waits. */
function owedByTone(item: JobStatusOpenItem): PillTone {
  return item.owed_by.kind === "staff" ? "attention" : "waiting";
}

/**
 * The walk-4 "What's left to do" panel: each OPEN item, who owes it, how to resolve it, when it is
 * due, and — through `actionsFor` — the buttons the viewer may press. The rows and their words are
 * the ENGINE's (`open_items[]`); the page adds only the layout and the due-date wording.
 *
 * <p>Card 2404: the rows are drawn in the order they ARRIVE. The engine ranks them in one function
 * the board's Needs you row shares (a failed check or a watchlist hit first, then staff decisions,
 * chases, and waiting on others), so a sort here would be a second ranking free to disagree with the
 * board. Each row carries its `tier` as `data-tier`, and the most important ones are marked.</p>
 *
 * <p>Card 2404: NO "To be supplied later" line under the rows. That line read out the engine's
 * `supply_later.labels_text`, which is built from the SAME ledger rows this panel lists (the open
 * client-owed rows, `OutstandingLedgerReconciler`), so every label in it was already a row above —
 * the walk saw each one twice, and some three times. A per-party item for someone who still owes
 * their whole form is folded into that person's identity-form row by the engine, and arrives with
 * the form.</p>
 */
function OpenItemsPanel({
  items,
  now,
  actionsFor,
  panelActions,
}: {
  items: readonly JobStatusOpenItem[];
  now: number;
  actionsFor?: (item: JobStatusOpenItem) => ReactNode;
  /** Actions on the whole panel — "Send reminders" to everyone owed. */
  panelActions?: ReactNode;
}): JSX.Element {
  const forYou = items.filter((item) => item.owed_by.kind === "staff").length;
  const withOthers = items.length - forYou;
  return (
    <Block
      title="What's left to do"
      className="fd-block--panel"
      meta={items.length === 0 ? "nothing left" : `${forYou} for you · ${withOthers} with others`}
      aside={panelActions}
    >
      {items.length === 0 ? (
        <p className="fd-panel__none">Nothing is left to do on this file.</p>
      ) : (
        <ul className="fd-todo">
          {items.map((item) => {
            const due = dueLine(item.due, now);
            const actions = actionsFor?.(item);
            return (
              <li key={item.key} className="fd-todo__item" data-tier={item.tier}>
                <span className="fd-todo__who">
                  <Pill tone={owedByTone(item)} fullLabel={owedByFullName(item)}>
                    {owedByLabel(item)}
                  </Pill>
                </span>
                <div className="fd-todo__what">
                  <span className="fd-todo__label">{item.label}</span>
                  <span className="fd-todo__how">
                    {item.how_to_resolve}
                    {due !== null && (
                      <span className={`fd-todo__due${due.overdue ? " fd-todo__due--overdue" : ""}`}>
                        {" "}
                        {due.text}.
                      </span>
                    )}
                  </span>
                </div>
                <div className="fd-todo__actions">{actions}</div>
              </li>
            );
          })}
        </ul>
      )}
    </Block>
  );
}

// ── People on this file (walk 4) ───────────────────────────────────────────────────────

interface PersonRow {
  id: string;
  person: JobStatusPerson;
  isCompany: boolean;
}

/**
 * Who is on the file and how each of them is going: their form (under the name, as the design
 * has it), their identity check and their screening. Every word is the engine's.
 */
function PeopleTable({
  people,
  parties,
}: {
  people: readonly JobStatusPerson[];
  parties: readonly JobStatusParty[];
}): JSX.Element {
  const rows: PersonRow[] = people.map((person) => ({
    id: person.party_key,
    person,
    isCompany: parties.some(
      (party) => party.iteration_key === person.party_key && party.party_type === "corporate_owner",
    ),
  }));
  const columns: Column<PersonRow>[] = [
    {
      key: "name",
      label: "Name",
      render: (row) => (
        <span className="fd-person">
          <span className="fd-person__name">
            {row.isCompany && <Icon name="building-2" size={16} aria-hidden="true" />}
            {row.person.name}
          </span>
          <span className="fd-person__form">{row.person.form.label}</span>
        </span>
      ),
    },
    { key: "role", label: "Role", render: (row) => row.person.role_label },
    {
      key: "id",
      label: "Identity check",
      render: (row) => <PersonStatus status={row.person.id_check} />,
    },
    {
      key: "screening",
      label: "Screening",
      render: (row) => <ScreeningStatus status={row.person.screening} />,
    },
  ];
  return (
    <Block title="People on this file">
      {people.length === 0 ? (
        <p className="fd-none">{NO_PARTIES}</p>
      ) : (
        <Table<PersonRow> columns={columns} rows={rows} caption="People on this file" />
      )}
    </Block>
  );
}

// ── What has happened ────────────────────────────────────────────────────────────────────

/**
 * The milestone spine in the platform's KINDS and the portal's English — phase 530's timeline. On
 * a walk-4 engine it is what "Show every event" reveals; on an older one it is the history.
 *
 * <p>Not exhaustive, and it says so: `other_event_count` is stated in words, and absent at zero.</p>
 */
function EventList({
  milestones,
  otherEventCount,
}: {
  milestones: readonly JobStatusMilestone[];
  otherEventCount: number;
}): JSX.Element {
  return (
    <>
      {milestones.length === 0 ? (
        <p className="fd-none">Nothing has been recorded on this file yet.</p>
      ) : (
        <ol className="fd-timeline">
          {milestones.map((milestone, index) => {
            const line = milestoneLine(milestone);
            return (
              <li
                // No per-row id on the wire; the list is rendered whole, in the order it arrived.
                key={`${index}-${line.kind}`}
                className={`fd-moment${line.unmapped ? " fd-moment--unmapped" : ""}`}
                data-milestone-kind={line.kind}
              >
                <span className="fd-moment__when">{formatDay(milestone.at)}</span>
                <span className="fd-moment__what">
                  {line.text}
                  {line.rawStage !== null && (
                    <span className="fb-stage fb-stage--raw fd-moment__stage">
                      <code className="fb-stage__state">{line.rawStage}</code>
                      <span className="fb-stage__note">{STAGE_NOT_NAMED}</span>
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {otherEventCount > 0 && (
        <p className="fd-timeline__rest">
          This shows the file&rsquo;s milestones. There {otherEventCount === 1 ? "is" : "are"}{" "}
          {otherEventCount} other recorded event{otherEventCount === 1 ? "" : "s"} on it as well.
        </p>
      )}
    </>
  );
}

/**
 * "What has happened": the walk-4 milestones (already plain words), newest first as the design
 * reads them, with "Show every event" one click away. Without walk-4 milestones, the phase 530
 * timeline is the history.
 */
function History({ detail, now }: { detail: JobStatusDetailResponse; now: number }): JSX.Element {
  const [everything, setEverything] = useState(false);
  const lines = detail.milestones;

  if (lines === undefined) {
    return (
      <Block title="What has happened">
        <EventList milestones={detail.timeline} otherEventCount={detail.other_event_count} />
      </Block>
    );
  }

  return (
    <Block
      title="What has happened"
      aside={
        <button
          type="button"
          className="fd-link fd-block__aside"
          aria-expanded={everything}
          onClick={() => setEverything((was) => !was)}
        >
          {everything ? "Show milestones only" : "Show every event"}
        </button>
      }
    >
      {everything ? (
        <EventList milestones={detail.timeline} otherEventCount={detail.other_event_count} />
      ) : lines.length === 0 ? (
        <p className="fd-none">Nothing has been recorded on this file yet.</p>
      ) : (
        <ol className="fd-history">
          {/* The contract sends them oldest first; the design reads newest first. */}
          {[...lines].reverse().map((line, index) => (
            <li key={`${index}-${line.at}`} className="fd-history__item">
              <span className="fd-history__when">{momentLabel(line.at, now)}</span>
              <span className="fd-history__dot" aria-hidden="true" />
              <span className="fd-history__what">{line.text}</span>
            </li>
          ))}
        </ol>
      )}
    </Block>
  );
}

// ── The party ledger (an engine older than walk 4) ────────────────────────────────────────

/** One row of the party ledger, as the table renders it. `id` is React's key, never copy. */
interface PartyRow {
  id: string;
  party: JobStatusParty;
}

/**
 * Phase 530's party ledger — who is registered on the file, what came back from each of them, and
 * whether they can be reached. The people table replaces it on a walk-4 engine. No email, ever.
 */
function PartyLedger({
  parties,
  partyCount,
  now,
}: {
  parties: readonly JobStatusParty[];
  partyCount: number;
  now: number;
}): JSX.Element {
  const rows: PartyRow[] = parties.map((party) => ({ id: party.iteration_key, party }));

  const columns: Column<PartyRow>[] = [
    {
      key: "party",
      label: "Name",
      render: (row) =>
        row.party.display_name !== null && row.party.display_name.trim() !== "" ? (
          <span className="fd-party__name">{row.party.display_name}</span>
        ) : (
          <span className="fd-none">no name recorded</span>
        ),
    },
    {
      key: "roles",
      label: "Roles",
      render: (row) =>
        row.party.roles.length === 0 ? (
          <span className="fd-none">{NOT_RECORDED}</span>
        ) : (
          <span className="fd-party__roles">
            {row.party.roles.map((role) => roleLabel(role)).join(", ")}
          </span>
        ),
    },
    {
      key: "came-back",
      label: "What came back",
      render: (row) => (
        <span className="fd-party__state">
          <PartyStatusChip party={row.party} />
          <span className="fd-party__since">
            {row.party.last_event === null
              ? "nothing recorded yet"
              : `last change ${owedForLabel(row.party.last_event.at, now)} ago`}
          </span>
        </span>
      ),
    },
    {
      key: "contact",
      label: "Contact",
      render: (row) => (
        <span className="fd-party__contact">{contactStateLabel(row.party.contact_state)}</span>
      ),
    },
  ];

  return (
    <Block
      title="People on this file"
      meta={partyCount === 1 ? "1 registered" : `${partyCount} registered`}
    >
      {partyCount === 0 ? (
        <p className="fd-none">{NO_PARTIES}</p>
      ) : (
        <Table<PartyRow> columns={columns} rows={rows} caption="People on this file" />
      )}
    </Block>
  );
}

/**
 * Phase 530's outstanding block — what the file owes and the record of what it no longer does,
 * waives included. The walk-4 panel replaces it on an engine that sends `open_items`.
 *
 * <p>Card 2404: the supply-later projection no longer repeats its labels here either — they are
 * the open rows of the list above (`outstanding_items` is that same ledger). What it still says is
 * the one thing the list cannot: that the workflow declares supply-later items and none is left.</p>
 */
function Outstanding({ detail, now }: { detail: JobStatusDetailResponse; now: number }): JSX.Element {
  const { outstanding, outstanding_items: items, supply_later: supplyLater } = detail;

  return (
    <Block
      title="What's left to do"
      meta={outstanding.clear ? "nothing owed" : `${outstanding.open_count} still owed`}
    >
      {items.length === 0 ? (
        <EmptyState
          iconName="check-circle-2"
          title="Nothing outstanding"
          body="This file is not waiting on anything, and nothing has been waived on it."
        />
      ) : (
        <ul className="oi-list">
          {items.map((item) => (
            <OutstandingItemLine key={outstandingItemKey(item)} item={item} now={now} />
          ))}
        </ul>
      )}

      {/* The DECLARED supply-later projection. ABSENT means the workflow declares none; `count: 0`
          with the object present is "declared, and none outstanding", which is news. A non-zero
          count is NOT said: its labels are rows of the list above (card 2404). */}
      {supplyLater !== undefined && supplyLater.count === 0 && (
        <p className="fd-supply">Nothing is left to be supplied later.</p>
      )}
    </Block>
  );
}

// ── The aside: about the client, and risk ────────────────────────────────────────────────

/**
 * What the platform knows about the file itself. First the facts the WORKFLOW declares about its
 * client (`about`, walk 4: e.g. company number, registered office, SIC codes, services), each
 * already in words and each omitted by the engine when the file does not hold it —
 * then when and how the file was opened, how long it has been open, and — when there is no stepper
 * to show it — the stage and the declared next step. An older engine sends no `about`, and the card
 * reads exactly as it did before.
 */
/**
 * The declared "About the client" lines worth drawing: a line with a blank label or value is
 * dropped quietly (the engine already omits an unresolved one; this keeps a blank from ever
 * rendering as an empty row or the word "undefined").
 */
export function aboutLines(detail: JobStatusDetailResponse): JobStatusAboutLine[] {
  return (detail.about ?? []).filter(
    (line) =>
      typeof line.label === "string" &&
      typeof line.value === "string" &&
      line.label.trim() !== "" &&
      line.value.trim() !== "",
  );
}

function AboutTheClient({
  detail,
  now,
  showStage,
}: {
  detail: JobStatusDetailResponse;
  now: number;
  showStage: boolean;
}): JSX.Element {
  const days = daysOpen(detail.opened_at, now);
  const via = arrivedVia(detail.opened_via);
  return (
    <section className="fd-card" aria-labelledby="fd-about-h">
      <h2 id="fd-about-h" className="fd-card__title">
        About the client
      </h2>
      <KV label="Client" value={fileDisplayName(detail.client_name).text} />
      {aboutLines(detail).map((line, index) => (
        <KV key={`${line.label}-${index}`} label={line.label} value={line.value} />
      ))}
      <KV label="Opened" value={`${formatDay(detail.opened_at)} (${daysOpenLabel(days)})`} />
      {/* Card #2909 — plain words, not a code chip: a webhook's declared readable source name
          ("GoProposal") verbatim, a platform start in words ("Scheduled run"), never a raw code. */}
      <KV
        label="Arrived via"
        value={via === null ? <span className="fd-none">{NOT_RECORDED}</span> : via}
      />
      {showStage && (
        <KV
          label="Stage"
          value={<StageValue label={detail.stage_label} source={detail.stage_label_source} />}
        />
      )}
      <KV label="Next step" value={<NextAction nextAction={detail.next_action} owner={detail.owner} />} />
    </section>
  );
}

/**
 * The risk assessment — or, honestly, its absence (AC-6). `band` is what the decision table
 * COMPUTED and `final_band` what the approver SIGNED OFF; both are shown when both exist. The
 * staff-form actions ("Start risk assessment", "Review and approve") are drawn on the open item
 * that carries them, like every other action, and only for a viewer allowed to take them.
 */
function Risk({ risk }: { risk: JobStatusRisk | undefined }): JSX.Element {
  return (
    <section className="fd-card" aria-labelledby="fd-risk-h">
      <h2 id="fd-risk-h" className="fd-card__title">
        Risk
      </h2>
      {risk === undefined ? (
        <p className="fd-muted">{NO_RISK}</p>
      ) : (
        <dl className="fd-facts">
          <div className="fd-fact">
            <dt className="fd-fact__k">Band</dt>
            <dd className="fd-fact__v">
              {risk.band === null ? (
                <span className="fd-none">{NOT_RECORDED}</span>
              ) : (
                <RiskBand band={risk.band} />
              )}
            </dd>
          </div>
          {risk.score !== null && (
            <div className="fd-fact">
              <dt className="fd-fact__k">Score</dt>
              <dd className="fd-fact__v num">{risk.score}</dd>
            </div>
          )}
          <div className="fd-fact">
            <dt className="fd-fact__k">Assessed by</dt>
            <dd className="fd-fact__v">
              {risk.assessed_by === null ? (
                <span className="fd-none">{NOT_RECORDED}</span>
              ) : (
                actorName(risk.assessed_by)
              )}
            </dd>
          </div>
          {risk.final_band !== undefined && (
            <div className="fd-fact">
              <dt className="fd-fact__k">Signed off as</dt>
              <dd className="fd-fact__v">
                <RiskBand band={risk.final_band} />
              </dd>
            </div>
          )}
          {risk.approved_by !== undefined && (
            <div className="fd-fact">
              <dt className="fd-fact__k">Approved by</dt>
              <dd className="fd-fact__v">
                {actorName(risk.approved_by)}
                {/* `same_actor` is OMITTED when false: the page states only what was attested. */}
                {risk.same_actor === true && (
                  <span className="fd-fact__note">the same person who assessed it</span>
                )}
              </dd>
            </div>
          )}
        </dl>
      )}
    </section>
  );
}

/** A band, in the word whoever computed it used; only its urgency tone is decided here. */
function RiskBand({ band }: { band: string }): JSX.Element {
  const known = ["low", "medium", "high"].includes(band.trim().toLowerCase());
  const tone = known ? band.trim().toLowerCase() : "other";
  return <span className={`fd-band fd-band--${tone}`}>{band}</span>;
}

// ── The file's state, for the header pill ────────────────────────────────────────────────

/** The header pill for the engine's own per-viewer answer (`attention`). */
const ATTENTION_PILL: Readonly<Record<JobStatusAttention, { label: string; tone: PillTone }>> = {
  needs_you: { label: "Needs you", tone: "attention" },
  waiting_on_others: { label: "Waiting on others", tone: "waiting" },
  done: { label: "Completed", tone: "good" },
};

/**
 * The file-level state the header pill says. A walk-4 engine sends its own answer for this viewer
 * (`attention`, the same rule the board reads), and the page says THAT — it never re-derives it
 * (card #2267). Only an engine that sends no `attention` gets the page's fallback: "Needs you" when
 * an item the VIEWER'S SIDE owes carries an action the engine allowed this viewer, "Waiting on
 * others" when items are open but none of those, and on an older engine still a staff-owned stage.
 */
export function fileAttention(
  detail: JobStatusDetailResponse,
): { label: string; tone: PillTone } | null {
  if (detail.attention !== undefined) {
    return ATTENTION_PILL[detail.attention] ?? null;
  }
  if (detail.is_terminal) return { label: "Completed", tone: "good" };
  if (detail.open_items !== undefined) {
    const needsYou = detail.open_items.some(
      (item) => item.owed_by.kind === "staff" && item.actions.some((action) => action.allowed),
    );
    if (needsYou) return { label: "Needs you", tone: "attention" };
    return detail.open_items.length > 0 ? { label: "Waiting on others", tone: "waiting" } : null;
  }
  return detail.owner === "staff" ? { label: "Needs you", tone: "attention" } : null;
}

/** The stepper's steps, from the contract's stage, with the design's short line under each. */
function stepperSteps(stage: JobStatusStage, attention: boolean): StageStep[] {
  return stage.steps.map((step) => ({
    key: step.key,
    label: step.label,
    state: step.state,
    detail:
      step.state === "done"
        ? "Done"
        : step.state === "current"
          ? attention
            ? "Needs you"
            : "In progress"
          : "Not started",
  }));
}

// ── The page ─────────────────────────────────────────────────────────────────────────────

export function FileDetailPage(): JSX.Element {
  // #2508: a file opened from a workflow's own board (`/boards/<workflow>/<id>`) goes back there.
  const { jobId, workflowId } = useParams<{ jobId: string; workflowId: string }>();
  const backTo = workflowId === undefined ? "/onboarding" : boardRoute(workflowId);
  const identity = useClientTenant();
  const tenantId = identity.tenantId;
  // The way back names the board it goes to ("Back to Clients"); `/onboarding` keeps its words.
  const families = useFamilies(tenantId);
  const board = workflowId === undefined ? undefined : boardFor(families.data?.families, workflowId);
  const backLabel = workflowId === undefined ? BACK_TO_BOARD : `Back to ${board?.label ?? "the board"}`;

  const file = useJobStatusDetail(tenantId, jobId);
  const data = file.data;

  const name = fileDisplayName(data?.client_name ?? null);
  // The tab names the FILE once there is a name to use, and the section otherwise — never the id.
  usePageTitle(data !== undefined && name.named ? name.text : ONBOARDING_LABEL);

  // A missing file and another tenant's file are ONE answer (404): "we could not find it".
  const notFound = file.error instanceof ApiError && file.error.status === 404;
  const failed = identity.error ?? (notFound ? null : file.error) ?? null;

  const unresolved =
    tenantId !== null && jobId !== undefined && data === undefined && file.error === null;
  const isBusy = identity.isLoading || unresolved;

  // Read once per answer, so every age on the page measures against the SAME instant.
  const now = useMemo(() => Date.now(), [data]);
  const attention = data === undefined ? null : fileAttention(data);
  // Walk 4 (#2268): the buttons beside each open item, the dialogs and the result toast.
  const actions = useFileActions({
    tenantId,
    jobId,
    detail: data,
    viewer: identity.email,
    refetch: () => void file.refetch(),
  });
  const opened = data === undefined ? null : openedLabel(data.opened_at, now);

  return (
    <div className="file-detail" data-testid="onboarding-file-detail">
      <Link className="fd-back" to={backTo}>
        <span aria-hidden="true">&larr;</span> {backLabel}
      </Link>

      <PageHeader
        title={data === undefined ? ONBOARDING_LABEL : name.text}
        subtitle={data === undefined ? undefined : opened ?? undefined}
        actions={
          <>
            {attention !== null && (
              <Pill tone={attention.tone} size="lg">
                {attention.label}
              </Pill>
            )}
            <Button
              variant="ghost"
              onClick={() => void file.refetch()}
              disabled={file.isFetching || !tenantId || jobId === undefined}
            >
              {file.isFetching ? "Refreshing…" : "Refresh"}
            </Button>
          </>
        }
      />

      {notFound && (
        <div className="state state--error" role="status">
          <h2 className="state__title">We could not find that file</h2>
          <p className="state__body">
            It may have been removed, or the link you followed may be out of date. Go back to your
            files and open it from there.
          </p>
        </div>
      )}

      {failed && (
        <div className="state state--error" role="status">
          <h2 className="state__title">We could not load this file</h2>
          <p className="state__body">
            Please try again in a moment. If it keeps happening, contact your AR Dept contact.
          </p>
        </div>
      )}

      {!failed && !notFound && isBusy && (
        <div className="fd-loading" aria-busy="true" aria-live="polite">
          <span className="fd-loading__label">Loading this file…</span>
          <div className="skeleton" style={{ width: "40%" }} />
          <div className="skeleton" style={{ width: "75%" }} />
          <div className="skeleton" />
        </div>
      )}

      {!failed && !notFound && !isBusy && data && (
        <>
          <LastUpdated timestamp={new Date(data.as_of)} />

          {actions.linkNotice !== null && (
            <div className="state" role="status" data-testid="deep-link-notice">
              <p className="state__body">{actions.linkNotice}</p>
            </div>
          )}

          {data.stage !== undefined && data.stage.steps.length > 0 && (
            <div className="fd-stepper">
              <StageStepper
                steps={stepperSteps(data.stage, attention?.label === "Needs you")}
                currentTone={attention?.label === "Needs you" ? "attention" : "active"}
              />
            </div>
          )}

          <div className="fd-layout">
            <div className="fd-layout__main">
              {/* Card 2508: the record's declared values and declared actions (the client page's
                  review card). Absent for a declaration with neither. */}
              <RecordSummary detail={data} tenantId={tenantId} jobId={jobId} viewer={identity.email} />
              {data.open_items !== undefined ? (
                <OpenItemsPanel
                  items={data.open_items}
                  now={now}
                  actionsFor={actions.actionsFor}
                  panelActions={actions.panelActions}
                />
              ) : (
                <Outstanding detail={data} now={now} />
              )}
              {data.people !== undefined ? (
                <PeopleTable people={data.people} parties={data.parties} />
              ) : (
                <PartyLedger parties={data.parties} partyCount={data.party_count} now={now} />
              )}
              <History detail={data} now={now} />
            </div>
            <aside className="fd-layout__aside">
              <AboutTheClient
                detail={data}
                now={now}
                showStage={data.stage === undefined || data.stage.steps.length === 0}
              />
              <Risk risk={data.risk} />
            </aside>
          </div>
        </>
      )}

      {actions.overlays}
    </div>
  );
}

/** What happens next, and who it is with. Both DECLARED; both omitted when undeclared. */
function NextAction({
  nextAction,
  owner,
}: {
  nextAction: string | null;
  owner: string | null;
}): JSX.Element {
  const who = ownerLabel(owner);
  if (nextAction === null && who === null) return <span className="fd-none">{NOT_RECORDED}</span>;
  return (
    <span className="fd-next">
      {nextAction !== null && <span className="fd-next__what">{nextAction}</span>}
      {who !== null && <span className="fd-next__who">with {who}</span>}
    </span>
  );
}

export default FileDetailPage;
